<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  field: {
    type: Object,
    default: null,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'close']);

const nameInputRef = ref(null);
const descriptionInputRef = ref(null);
const possibleValueDraft = ref('');

const form = ref({
  name: '',
  description: '',
  possibleValues: [],
});

const isEditMode = computed(() => Boolean(props.field));

const nameLocked = computed(() => {
  if (!props.field) return false;
  return props.field.editable_name === false;
});

const sourceHint = computed(() => {
  if (!props.field) return '';
  if (props.field.source_type === 'bot') return t('OMNI.SLOTS.FIELD_SOURCE_BOT');
  if (props.field.source_slot) {
    return t('OMNI.SLOTS.FIELD_SOURCE_SLOT', { slot: props.field.source_slot });
  }
  return '';
});

const canSave = computed(() => {
  const trimmedName = form.value.name.trim();
  if (!trimmedName) return false;
  if (!nameLocked.value && !/^[a-z][a-z0-9_]*$/.test(trimmedName)) return false;
  return !props.saving;
});

const resetForm = () => {
  if (props.field) {
    form.value = {
      name: props.field.field_name || '',
      description: props.field.description || '',
      possibleValues: Array.isArray(props.field.possible_values)
        ? [...props.field.possible_values]
        : [],
    };
  } else {
    form.value = { name: '', description: '', possibleValues: [] };
  }
  possibleValueDraft.value = '';
};

watch(
  () => props.isOpen,
  open => {
    if (open) {
      resetForm();
      nextTick(() => {
        if (nameLocked.value) {
          descriptionInputRef.value?.focus();
        } else {
          nameInputRef.value?.focus();
        }
      });
    }
  }
);

const addPossibleValueFromDraft = () => {
  const raw = possibleValueDraft.value.trim();
  if (!raw) return;
  raw
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)
    .forEach(v => {
      if (!form.value.possibleValues.includes(v)) {
        form.value.possibleValues.push(v);
      }
    });
  possibleValueDraft.value = '';
};

const handlePossibleValueKeydown = event => {
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault();
    addPossibleValueFromDraft();
  } else if (event.key === 'Backspace' && !possibleValueDraft.value) {
    form.value.possibleValues.pop();
  }
};

const removePossibleValue = index => {
  form.value.possibleValues.splice(index, 1);
};

const handleSave = () => {
  if (!canSave.value) return;
  if (possibleValueDraft.value.trim()) addPossibleValueFromDraft();

  emit('save', {
    name: form.value.name.trim(),
    description: form.value.description,
    possibleValues: form.value.possibleValues,
    nameLocked: nameLocked.value,
    isEdit: isEditMode.value,
    originalName: props.field?.field_name || null,
  });
};

const handleClose = () => {
  if (props.saving) return;
  emit('close');
};
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @keydown.esc="handleClose"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
    >
      <div
        class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between"
      >
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ isEditMode ? t('OMNI.SLOTS.FIELD_FORM_EDIT_TITLE') : t('OMNI.SLOTS.FIELD_FORM_NEW_TITLE') }}
        </h2>
        <button
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          :disabled="saving"
          @click="handleClose"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <div class="px-6 py-4 overflow-y-auto flex-1 space-y-4">
        <p
          v-if="sourceHint"
          class="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5"
        >
          <span
            v-if="field?.source_type === 'bot'"
            class="i-lucide-sparkles size-3.5 text-woot-500"
          />
          <span
            v-else
            class="i-lucide-link-2 size-3.5 text-slate-400"
          />
          {{ sourceHint }}
        </p>

        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
            {{ t('OMNI.SLOTS.FIELD_NAME') }}
          </label>
          <input
            ref="nameInputRef"
            v-model="form.name"
            type="text"
            :disabled="nameLocked"
            :placeholder="t('OMNI.SLOTS.FIELD_NAME_PLACEHOLDER')"
            class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:text-slate-500 disabled:cursor-not-allowed font-mono"
          />
          <p
            v-if="nameLocked"
            class="mt-1 text-xs text-slate-500 dark:text-slate-400"
          >
            {{ t('OMNI.SLOTS.FIELD_NAME_LOCKED') }}
          </p>
          <p
            v-else
            class="mt-1 text-xs text-slate-500 dark:text-slate-400"
          >
            {{ t('OMNI.SLOTS.FIELD_NAME_HELP') }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
            {{ t('OMNI.SLOTS.FIELD_DESCRIPTION') }}
          </label>
          <textarea
            ref="descriptionInputRef"
            v-model="form.description"
            rows="3"
            :placeholder="t('OMNI.SLOTS.FIELD_DESCRIPTION_PLACEHOLDER')"
            class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent resize-none"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
            {{ t('OMNI.SLOTS.FIELD_POSSIBLE_VALUES') }}
          </label>
          <div
            v-if="form.possibleValues.length"
            class="flex flex-wrap gap-1.5 mb-2 max-h-32 overflow-y-auto p-2 bg-slate-50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-slate-700 rounded-md"
          >
            <span
              v-for="(value, index) in form.possibleValues"
              :key="`${value}-${index}`"
              class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs rounded-full bg-woot-100 text-woot-700 dark:bg-woot-900/40 dark:text-woot-300 border border-woot-200 dark:border-woot-800"
            >
              <span class="font-medium">{{ value }}</span>
              <button
                type="button"
                class="inline-flex items-center justify-center size-4 rounded-full text-woot-500 hover:text-white hover:bg-woot-500 dark:hover:bg-woot-400 transition-colors"
                :title="t('OMNI.SLOTS.DELETE_FIELD')"
                @click="removePossibleValue(index)"
              >
                <span class="i-lucide-x size-3" />
              </button>
            </span>
          </div>
          <div
            class="flex items-center gap-1 px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md focus-within:ring-2 focus-within:ring-woot-500 focus-within:border-transparent"
          >
            <span class="i-lucide-tag size-4 text-slate-400 flex-shrink-0" />
            <input
              v-model="possibleValueDraft"
              type="text"
              :placeholder="t('OMNI.SLOTS.FIELD_POSSIBLE_VALUES_PLACEHOLDER')"
              class="flex-1 min-w-0 px-1 py-0.5 text-sm bg-transparent focus:outline-none text-slate-700 dark:text-slate-200"
              @keydown="handlePossibleValueKeydown"
              @blur="addPossibleValueFromDraft"
            />
            <button
              v-if="possibleValueDraft.trim()"
              type="button"
              class="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium text-woot-600 hover:text-woot-700 hover:bg-woot-50 dark:text-woot-400 dark:hover:bg-woot-900/30 rounded"
              @click="addPossibleValueFromDraft"
            >
              <span class="i-lucide-plus size-3.5" />
            </button>
          </div>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {{ t('OMNI.SLOTS.FIELD_POSSIBLE_VALUES_HINT') }}
          </p>
        </div>
      </div>

      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2 bg-slate-50 dark:bg-slate-900/40"
      >
        <button
          class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          :disabled="saving"
          @click="handleClose"
        >
          {{ t('OMNI.COMMON.CANCEL') }}
        </button>
        <button
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed rounded-md transition-colors"
          :disabled="!canSave"
          @click="handleSave"
        >
          <span
            v-if="saving"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          {{ t('OMNI.SLOTS.FIELD_SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
